/**
 * AvatarUpload — click to pick an image; it's center-cropped + resized in the
 * browser to a small square dataURL and saved to the user profile (avatarUrl).
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, X, Loader2 } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { notify } from '../lib/toast';
import { cn } from '../lib/utils';

/** Center-crop to a square and resize to `size`px, returns a JPEG dataURL. */
function resizeToDataUrl(file: File, size = 256): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('no-ctx');
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image-load-failed'));
    };
    img.src = url;
  });
}

interface AvatarUploadProps {
  /** Pixel size of the avatar box. */
  size?: number;
  className?: string;
}

export function AvatarUpload({ size = 56, className }: AvatarUploadProps) {
  const { t } = useTranslation();
  const { user, updateProfile } = useAuthStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const initial = user?.name?.charAt(0).toUpperCase() ?? 'U';

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      notify.error(t('avatarUpload.selectImageFile', 'Selecione um arquivo de imagem.'));
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await resizeToDataUrl(file);
      const ok = await updateProfile({ avatarUrl: dataUrl });
      if (ok) notify.success(t('avatarUpload.updated', 'Avatar atualizado!'));
      else notify.error(t('avatarUpload.saveFailed', 'Falha ao salvar o avatar.'));
    } catch {
      notify.error(t('avatarUpload.processFailed', 'Não foi possível processar a imagem.'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const ok = await updateProfile({ avatarUrl: null });
      if (ok) notify.success(t('avatarUpload.removed', 'Avatar removido.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn('relative shrink-0', className)} style={{ width: size, height: size }}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        title={t('avatarUpload.changeAvatar', 'Trocar avatar')}
        className="group relative h-full w-full overflow-hidden rounded-xl ring-1 ring-border shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {user?.avatarUrl ? (
          <img src={user.avatarUrl} alt={t('avatarUpload.avatarAlt', 'Avatar')} className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center bg-gradient-to-br from-primary to-accent text-primary-foreground">
            <span className="font-serif text-xl font-bold">{initial}</span>
          </div>
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 grid place-items-center bg-black/45 text-white opacity-0 transition-opacity group-hover:opacity-100">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
        </div>
      </button>

      {user?.avatarUrl && !busy && (
        <button
          type="button"
          onClick={handleRemove}
          title={t('avatarUpload.removeAvatar', 'Remover avatar')}
          className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:bg-destructive hover:text-destructive-foreground"
        >
          <X size={11} />
        </button>
      )}

      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
}
